import { faCalendarClock } from "@fortawesome/pro-regular-svg-icons";

import { Feature, UISubscriptionStatus, UserSubscriptionAction } from "common/types";
import { toDate, toRelativeDate } from "common/utils/dates";
import { useBuildNext, useSearchState } from "common/router/hooks";
import Icon from "common/components/Icon";

import {
  OnboardingDetailSubscriptionStepParams,
  OnboardingSubscriptionNextOrderDateStepParams,
} from "client/onboarding/types";
import { ModalStatus } from "client/common/types";
import Button from "client/common/components/buttons/Button";
import { useFeatures } from "client/clinics/hooks";
import { useSubscriptionQuery, useUpdateSubscriptionMutation } from "client/subscriptions/hooks";
import LoadingContainer from "client/common/components/loading/LoadingContainer";
import FetchErrorAlert from "client/common/components/errors/FetchErrorAlert";
import Error404Page from "client/common/components/errors/Error404Page";
import { useInvalidateOnboarding } from "client/onboarding/hooks/invalidate";
import { Paths } from "client/routes";

import UpdateNextOrderDateModal from "../modals/UpdateNextOrderDateModal";
import StepLayout from "../OnboardingSubscriptionsStepLayout";

import styles from "../../styles/OnboardingSubscriptionNextOrderDateStep.module.less";

const OnboardingSubscriptionNextOrderDateStep = ({
  id,
  onNext,
  isLoadingNext,
  stepper,
  rootDest,
}: OnboardingDetailSubscriptionStepParams) => {
  const buildNext = useBuildNext();
  const isEdbEnabled = useFeatures()[Feature.AutoshipEDB];
  const [params, setParams] = useSearchState<Paths["onboarding"]["subscriptions"]["detail"]>({
    updateNextOrderDateModalIsVisible: ModalStatus.Closed,
  });
  const invalidateOnboarding = useInvalidateOnboarding();
  const {
    mutate: updateSubscription,
    isLoading: isUpdating,
    error: updateError,
  } = useUpdateSubscriptionMutation(id, {
    onSuccess: () => {
      invalidateOnboarding();
      onNext();
    },
  });
  const { data: subscription, isLoading: isSubscriptionLoading } = useSubscriptionQuery(id);

  if (isSubscriptionLoading) {
    return <LoadingContainer />;
  }
  if (!subscription) {
    return (
      <Error404Page
        title="Autoship Not Found!"
        description="We could not find that Autoship"
        buttonText="Update other Autoships"
        buttonLink={buildNext(rootDest)}
      />
    );
  }

  const setModalVisiblity = (
    param: keyof OnboardingSubscriptionNextOrderDateStepParams,
    visibility: ModalStatus,
  ) => setParams({ ...params, [param]: visibility });
  const canContinue = !isUpdating && !isLoadingNext;

  // In v2 the imported sub already carries an EDB (set at import), so show it. Old-world shows NOD.
  const displayDate = isEdbEnabled
    ? subscription.expected_delivery_by
    : subscription.next_order_date;

  return (
    <StepLayout stepper={stepper}>
      {!!updateError && (
        <FetchErrorAlert error={updateError} fieldErrors={[updateError.errors.action]} />
      )}
      <StepLayout.Title>
        {isEdbEnabled
          ? "Great! Does this date work for your next estimated delivery?"
          : "Great! Does this date work to ship your next order?"}
      </StepLayout.Title>
      {displayDate && (
        <div className={styles.banner}>
          <div className={styles.icon}>
            <Icon icon={faCalendarClock} />
          </div>
          <div className={styles.content}>
            <div className={styles.date}>{toDate(displayDate)}</div>
            <div className={styles.relative}>{toRelativeDate(displayDate)}</div>
          </div>
        </div>
      )}
      <StepLayout.Footer>
        <Button
          variant="secondary"
          onClick={() => setModalVisiblity("updateNextOrderDateModalIsVisible", ModalStatus.Open)}
        >
          I need it another time...
        </Button>
        <Button
          onClick={() =>
            /* if subscription is imported we want to activate it; if it's already activated we
                 just want to modify it to prevent an error from activating an already active
                 subscription */
            // TODO(HD2-981): on v2 (AUTOSHIP_EDB) RESUME requires expected_delivery_by; this
            // submits only next_order_date and 400s on v2 clinics. Submit expected_delivery_by.
            updateSubscription({
              action:
                subscription.visible_status === UISubscriptionStatus.Imported
                  ? UserSubscriptionAction.Resume
                  : UserSubscriptionAction.Modify,
              next_order_date: subscription.next_order_date ?? undefined,
              frequency: subscription.active_frequency.frequency,
              interval_unit: subscription.active_frequency.interval_unit,
            })
          }
          isLoading={isUpdating || isLoadingNext}
          disabled={!canContinue}
        >
          Continue
        </Button>
      </StepLayout.Footer>
      <UpdateNextOrderDateModal
        isVisible={params.updateNextOrderDateModalIsVisible === ModalStatus.Open}
        onClose={() => setModalVisiblity("updateNextOrderDateModalIsVisible", ModalStatus.Closed)}
        subscriptionId={id}
        initialNextOrderDate={subscription.next_order_date}
      />
    </StepLayout>
  );
};

export default OnboardingSubscriptionNextOrderDateStep;
